import { useCallback, useEffect, useRef } from 'react';
import type { NotificationAlertReceipt } from '../types/notification-alert.types';
import { notificationAlertReceiptSchema } from '../schemas/notification-alert-receipt.schema';

/** Shares only presented message identities across this browser's same-account tabs. */
export const useNotificationAlertDeduplication = (account: string | null) => {
  const seen = useRef(new Set<string>());
  const channel = useRef<BroadcastChannel | null>(null);
  const remember = useCallback((receipt: NotificationAlertReceipt) => {
    seen.current.add(
      JSON.stringify([receipt.tenantId, receipt.userId, receipt.messageId])
    );
    if (seen.current.size > 500) {
      const oldest = seen.current.values().next().value;
      if (oldest) seen.current.delete(oldest);
    }
  }, []);
  const hasSeen = useCallback(
    (receipt: NotificationAlertReceipt) =>
      seen.current.has(
        JSON.stringify([receipt.tenantId, receipt.userId, receipt.messageId])
      ),
    []
  );
  const broadcast = useCallback((receipt: NotificationAlertReceipt) => {
    try {
      channel.current?.postMessage(receipt);
    } catch {
      /* Local focus/deduplication still applies when this optional channel is unavailable. */
    }
  }, []);

  useEffect(() => {
    seen.current.clear();
    if (!account || typeof BroadcastChannel === 'undefined') return;
    let current: BroadcastChannel;
    try {
      current = new BroadcastChannel(`comos:notification-alerts:${account}`);
    } catch {
      return;
    }
    channel.current = current;
    const receive = (event: MessageEvent<unknown>) => {
      const parsed = notificationAlertReceiptSchema.safeParse(event.data);
      if (
        parsed.success &&
        `${parsed.data.tenantId}:${parsed.data.userId}` === account
      ) {
        remember(parsed.data);
      }
    };
    current.addEventListener('message', receive);
    return () => {
      current.removeEventListener('message', receive);
      current.close();
      if (channel.current === current) channel.current = null;
    };
  }, [account, remember]);
  return { hasSeen, remember, broadcast };
};
